/**
 * CREW MEMBERS CARD - Liste des membres d'un équipage (max 2)
 */

import { useAuth } from '@/hooks/useAuth';
import { useContactActions } from '@/hooks/useContactActions';
import { useCrewActions } from '@/hooks/useCrewActions';
import { useProspects } from '@/hooks/useProspects';
import { useSupabase } from '@/hooks/useSupabase';
import type { Contact } from '@/types/contact';
import type { ContactWithRole, Crew, CrewMemberRole } from '@/types/crew';
import { CREW_MEMBER_ROLE } from '@/types/crew';
import { Copy, Link, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AddContactCard, ContactCard } from '../contact';
import { ConfirmDialog, Modal, ModalFooter, toast } from '../ui';
import { Checkbox } from '../ui/checkbox';
import { isOver28AtEventDate } from '@/lib/age-utils';
import { buildSearchOrConditions } from '@/lib/postgrestSearch';
import { ROUTES } from '@/lib/routes/constants';
import { RoleBadge } from './RoleBadge';

interface CrewMembersCardProps {
  crew: Crew;
  onUpdate: () => void;
  onEmailClick?: (contactId: string) => void;
  onCallClick?: (contactId: string) => void;
}

export function CrewMembersCard({
  crew,
  onUpdate,
  onEmailClick,
  onCallClick,
}: CrewMembersCardProps) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const canValidateDerogation =
    user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superadmin';
  const { getContactById, updateContact } = useContactActions();
  const { addCrewMember, updateCrewMemberRole, removeCrewMember, updateDerogationValidated } =
    useCrewActions();
  const { deleteProspectByContactAndProduct } = useProspects({ mode: 'server' });

  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function searchContacts(term: string) {
    const trimmed = term.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setLoadingContacts(false);
      setContactsError(null);
      return;
    }

    setLoadingContacts(true);
    setContactsError(null);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone')
        .or(buildSearchOrConditions(['first_name', 'last_name', 'email', 'phone'], trimmed))
        .order('first_name', { ascending: true })
        .limit(100);

      if (error) {
        console.error('[CrewMembersCard] searchContacts error:', error);
        setContactsError(`Erreur: ${error.message}`);
        setSearchResults([]);
      } else {
        setSearchResults((data as Contact[]) || []);
      }
    } catch (err) {
      console.error('[CrewMembersCard] searchContacts exception:', err);
      setContactsError('Erreur lors de la recherche');
      setSearchResults([]);
    } finally {
      setLoadingContacts(false);
    }
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (value.trim().length < 2) {
      setSearchResults([]);
      setLoadingContacts(false);
      return;
    }
    setLoadingContacts(true);
    searchDebounceRef.current = setTimeout(() => searchContacts(value), 300);
  }

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Trier les membres : Pilote en premier, puis Copilote, puis sans rôle
  const sortedMembers = useMemo(() => {
    const members = (crew.members || []) as ContactWithRole[];
    return [...members].sort((a, b) => {
      // Pilote en premier
      if (a.role === CREW_MEMBER_ROLE.PILOT) return -1;
      if (b.role === CREW_MEMBER_ROLE.PILOT) return 1;
      // Copilote ensuite
      if (a.role === CREW_MEMBER_ROLE.COPILOT) return -1;
      if (b.role === CREW_MEMBER_ROLE.COPILOT) return 1;
      return 0;
    });
  }, [crew.members]);

  const eventDate = (crew.product as any)?.start_date ?? null;

  async function handleDerogationChange(contactId: string, validated: boolean) {
    const ok = await updateDerogationValidated(crew.id, contactId, validated);
    if (ok) onUpdate();
    else toast.error('Erreur lors de la mise à jour de la dérogation');
  }

  function memberCardProps(member: ContactWithRole) {
    const over28 = isOver28AtEventDate(member.birth_date, eventDate);
    const validated = member.derogation_validated ?? false;
    return {
      tagContent:
        over28 && !validated ? (
          <span className="inline-block text-xs font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
            Dérogation en attente
          </span>
        ) : undefined,
      bottomContent:
        over28 && canValidateDerogation ? (
          <Checkbox
            checked={validated}
            onChange={(checked) => handleDerogationChange(member.id, checked)}
            label="Dérogation validée"
          />
        ) : undefined,
    };
  }

  // Trouver pilot, copilot, et membres sans rôle
  const pilot = sortedMembers.find((m) => m.role === CREW_MEMBER_ROLE.PILOT) || null;
  const copilot = sortedMembers.find((m) => m.role === CREW_MEMBER_ROLE.COPILOT) || null;
  const membersWithoutRole = sortedMembers.filter((m) => !m.role);

  // Si pas de pilot mais un membre sans rôle, l'utiliser comme "pilot potentiel"
  const displayPilot = pilot || membersWithoutRole[0] || null;
  // Si pas de copilot mais un 2ème membre sans rôle (ou le 1er si pilot existe), l'utiliser
  const displayCopilot = copilot || (pilot ? membersWithoutRole[0] : membersWithoutRole[1]) || null;

  // Auto-assigner les rôles manquants en base (une seule fois)
  const [hasFixedRoles, setHasFixedRoles] = useState(false);

  useEffect(() => {
    async function fixMissingRoles() {
      if (membersWithoutRole.length === 0 || hasFixedRoles) return;

      setHasFixedRoles(true);

      // Premier membre sans rôle → Pilote (si pas de pilote) sinon Copilote
      const firstWithoutRole = membersWithoutRole[0];
      if (firstWithoutRole) {
        const roleToAssign = !pilot ? CREW_MEMBER_ROLE.PILOT : CREW_MEMBER_ROLE.COPILOT;
        await updateCrewMemberRole(crew.id, firstWithoutRole.id, roleToAssign);
      }

      // Deuxième membre sans rôle → toujours Copilote
      const secondWithoutRole = membersWithoutRole[1];
      if (secondWithoutRole) {
        await updateCrewMemberRole(crew.id, secondWithoutRole.id, CREW_MEMBER_ROLE.COPILOT);
      }

      // Refresh les données
      onUpdate();
    }

    fixMissingRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler pour changer le rôle d'un membre (avec swap automatique)
  async function handleRoleChange(contactId: string, newRole: CrewMemberRole) {
    // Trouver l'autre membre qui a déjà ce rôle
    const otherMember = sortedMembers.find((m) => m.role === newRole && m.id !== contactId);
    const currentMember = sortedMembers.find((m) => m.id === contactId);

    // Si l'autre membre a déjà ce rôle, on doit swapper
    if (otherMember && currentMember) {
      const oppositeRole =
        newRole === CREW_MEMBER_ROLE.PILOT ? CREW_MEMBER_ROLE.COPILOT : CREW_MEMBER_ROLE.PILOT;

      // Mettre à jour les deux rôles
      const success1 = await updateCrewMemberRole(crew.id, contactId, newRole);
      const success2 = await updateCrewMemberRole(crew.id, otherMember.id, oppositeRole);

      if (success1 && success2) {
        toast.success('Rôles échangés');
        onUpdate();
      } else {
        toast.error('Erreur lors de la mise à jour des rôles');
      }
    } else {
      // Pas de swap nécessaire, juste mettre à jour le rôle
      const success = await updateCrewMemberRole(crew.id, contactId, newRole);
      if (success) {
        toast.success('Rôle mis à jour');
        onUpdate();
      } else {
        toast.error('Erreur lors de la mise à jour du rôle');
      }
    }
  }

  // Ouvrir la confirmation de retrait
  function openRemoveConfirm(contactId: string) {
    setSelectedContactId(contactId);
    setShowRemoveConfirm(true);
  }

  // Séparer les résultats de recherche : disponibles vs déjà membres
  const { availableContacts, alreadyMemberContacts } = useMemo(() => {
    const isCurrentMember = (contactId: string) =>
      crew.members?.some((member) => member.id === contactId) ?? false;

    return {
      availableContacts: searchResults.filter((c) => !isCurrentMember(c.id)),
      alreadyMemberContacts: searchResults.filter((c) => isCurrentMember(c.id)),
    };
  }, [searchResults, crew.members]);

  async function handleAddMember(contactId: string) {
    if (sortedMembers.length >= 2) {
      toast.error('Un équipage ne peut avoir que 2 membres maximum');
      return;
    }

    setIsAdding(true);
    try {
      // Déterminer le rôle automatiquement :
      // - Si pas de pilote → nouveau membre devient Pilote
      // - Sinon → nouveau membre devient Copilote
      const role = !pilot ? CREW_MEMBER_ROLE.PILOT : CREW_MEMBER_ROLE.COPILOT;

      const success = await addCrewMember(crew.id, contactId, role);

      if (success) {
        // ✅ Supprimer le prospect correspondant au produit de l'équipage
        if (crew.product_id) {
          await deleteProspectByContactAndProduct(contactId, crew.product_id);

          // Si l'équipage a un produit, l'ajouter au contact si pas déjà associé
          const contact = await getContactById(contactId);
          if (contact) {
            const currentProductIds = contact.products?.map((p: any) => p.id) || [];

            // Vérifier que le produit n'est pas déjà associé
            if (!currentProductIds.includes(crew.product_id)) {
              const updatedProductIds = [...currentProductIds, crew.product_id];
              // eslint-disable-next-line no-console
              console.log('Adding crew product to contact from crew detail', {
                contactId,
                crewId: crew.id,
                crewProductId: crew.product_id,
                updatedProductIds,
              });
              await updateContact(contactId, { product_ids: updatedProductIds } as any);
            }
          }
        }

        const roleLabel = role === CREW_MEMBER_ROLE.PILOT ? 'Pilote' : 'Copilote';
        toast.success(`${roleLabel} ajouté avec succès`);
        setShowAddModal(false);
        onUpdate();
      } else {
        toast.error("Erreur lors de l'ajout du membre");
      }
    } catch (error) {
      console.error('Error adding crew member:', error);
      toast.error("Erreur lors de l'ajout du membre");
    } finally {
      setIsAdding(false);
    }
  }

  async function handleRemoveMember() {
    if (!selectedContactId) return;

    const success = await removeCrewMember(crew.id, selectedContactId);
    if (success) {
      toast.success('Membre retiré avec succès');
      setShowRemoveConfirm(false);
      setSelectedContactId(null);
      onUpdate();
    } else {
      toast.error('Erreur lors du retrait du membre');
    }
  }

  function openAddModal() {
    if (sortedMembers.length >= 2) {
      toast.error('Un équipage ne peut avoir que 2 membres maximum');
      return;
    }
    setShowAddModal(true);
  }

  function closeAddModal() {
    setSearchTerm('');
    setSearchResults([]);
    setContactsError(null);
    setShowAddModal(false);
  }

  const inviteUrl = `${window.location.origin}${ROUTES.PUBLIC.ADD_TEAMMATE(crew.id)}`;

  function copyInviteLink() {
    navigator.clipboard.writeText(inviteUrl).then(
      () => toast.success('Lien copié dans le presse-papiers'),
      () => toast.error('Impossible de copier le lien')
    );
  }

  return (
    <div>
      {/* Lien d'invitation — visible uniquement quand l'équipage n'a qu'un seul membre */}
      {sortedMembers.length === 1 && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
          <Link className="h-4 w-4 flex-shrink-0 text-zinc-400" />
          <span className="min-w-0 flex-1 truncate text-xs text-zinc-500">{inviteUrl}</span>
          <button
            type="button"
            onClick={copyInviteLink}
            className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-zinc-700 hover:text-zinc-900"
          >
            <Copy className="h-3.5 w-3.5" />
            Copier
          </button>
        </div>
      )}

      {/* Affichage des membres - Pilote toujours en premier */}
      <div className="space-y-2">
        {/* Pilote (toujours en haut) */}
        {displayPilot ? (
          <ContactCard
            contact={displayPilot}
            onEmailClick={onEmailClick ? () => onEmailClick(displayPilot.id) : undefined}
            onCallClick={onCallClick ? () => onCallClick(displayPilot.id) : undefined}
            clickable
            roleContent={
              <RoleBadge
                role={displayPilot.role}
                onRoleChange={(role) => handleRoleChange(displayPilot.id, role)}
              />
            }
            onRemove={() => openRemoveConfirm(displayPilot.id)}
            {...memberCardProps(displayPilot)}
          />
        ) : (
          <AddContactCard onClick={openAddModal} label="Ajouter le pilote" />
        )}

        {/* Copilote (toujours en bas) */}
        {displayCopilot ? (
          <ContactCard
            contact={displayCopilot}
            onEmailClick={onEmailClick ? () => onEmailClick(displayCopilot.id) : undefined}
            onCallClick={onCallClick ? () => onCallClick(displayCopilot.id) : undefined}
            clickable
            roleContent={
              <RoleBadge
                role={displayCopilot.role}
                onRoleChange={(role) => handleRoleChange(displayCopilot.id, role)}
              />
            }
            onRemove={() => openRemoveConfirm(displayCopilot.id)}
            {...memberCardProps(displayCopilot)}
          />
        ) : (
          <AddContactCard onClick={openAddModal} label="Ajouter le copilote" />
        )}
      </div>

      {/* Modal d'ajout de membre */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title="Ajouter un membre"
        size="md"
        footer={
          <ModalFooter onCancel={closeAddModal} cancelLabel="Annuler" isSubmitting={isAdding} />
        }
      >
        {/* Champ de recherche */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Rechercher un contact (min. 2 caractères)..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
              autoFocus
            />
          </div>
        </div>

        {loadingContacts ? (
          <div className="text-center py-8 text-sm text-zinc-500">Chargement...</div>
        ) : contactsError ? (
          <div className="text-center py-8 text-sm text-red-500">{contactsError}</div>
        ) : availableContacts.length > 0 || alreadyMemberContacts.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {availableContacts.map((contact) => (
              <button
                key={contact.id}
                onClick={() => handleAddMember(contact.id)}
                disabled={isAdding}
                className="w-full text-left p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="font-medium text-zinc-900">
                  {contact.first_name} {contact.last_name}
                </div>
                {contact.email && <div className="text-sm text-zinc-600 mt-1">{contact.email}</div>}
                {contact.phone && (
                  <div className="text-sm text-zinc-500 mt-0.5">{contact.phone}</div>
                )}
              </button>
            ))}
            {alreadyMemberContacts.map((contact) => (
              <div
                key={contact.id}
                className="w-full text-left p-3 rounded-lg border border-zinc-100 bg-zinc-50 opacity-60 cursor-not-allowed"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-700">
                      {contact.first_name} {contact.last_name}
                    </div>
                    {contact.email && (
                      <div className="text-sm text-zinc-500 mt-1">{contact.email}</div>
                    )}
                  </div>
                  <span className="text-xs text-zinc-500 flex-shrink-0 italic">Déjà membre</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-sm text-zinc-500">
            {searchTerm.trim().length >= 2
              ? 'Aucun contact trouvé'
              : 'Tapez au moins 2 caractères pour rechercher'}
          </div>
        )}
      </Modal>

      {/* Dialog de confirmation de retrait */}
      <ConfirmDialog
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={handleRemoveMember}
        title="Retirer le membre"
        description="Êtes-vous sûr de vouloir retirer ce membre de l'équipage ?"
        confirmLabel="Retirer"
        cancelLabel="Annuler"
      />
    </div>
  );
}
